import cloneDeep from 'lodash/cloneDeep';
import { CriteriaState, CapTypes, CapInstance, CapRestriction, RestrictionTypes } from './types';
import { DAY_TO_DOW_TYPE, DOW_TYPE_TO_DAY } from './constants';

interface FieldSchema {
  type: 'boolean' | 'string' | 'number' | 'array' | 'object' | 'number|null';
  defaultValue: boolean | string | number | unknown[] | object | null;
  nested?: Record<string, FieldSchema>;
}

const CRITERIA_STATE_SCHEMA: Record<keyof CriteriaState, FieldSchema> = {
  // Validation & Compliance
  tcpaOptInRequired: { type: 'boolean', defaultValue: false },
  tcpaLastOptInInterval: { type: 'boolean', defaultValue: false },
  briteVerifyPhoneValidated: { type: 'boolean', defaultValue: false },
  briteVerifyEmailValidated: { type: 'boolean', defaultValue: false },
  phoneBlacklist: { type: 'boolean', defaultValue: false },
  gpc: { type: 'boolean', defaultValue: false },
  englishBrowserLanguageRequired: { type: 'boolean', defaultValue: false },
  externalPrePingProcessingEnabled: { type: 'boolean', defaultValue: false },

  // Demographics
  minAge: { type: 'string', defaultValue: '' },
  maxAge: { type: 'string', defaultValue: '' },
  browserType: { type: 'string', defaultValue: '' },
  genderValue: { type: 'string', defaultValue: '' },
  anuraScore: { type: 'string', defaultValue: '' },

  // User Data
  requiredUserDataIncludeExclude: { type: 'string', defaultValue: '' },
  requiredUserDataValues: { type: 'array', defaultValue: [] },

  // Job Keyword
  jobKeyword: {
    type: 'object',
    defaultValue: {
      includeExclude: '',
      jobKeywords: [],
    },
  },

  // Previous Registrations
  previousRegistrations: {
    type: 'object',
    defaultValue: {
      andOrLogic: 'AND' as const,
      previousRegistrations: [],
    },
  },

  // Lists & Segments
  nullSourceNewUserIncludeExclude: { type: 'string', defaultValue: '' },
  aibListIncludeExclude: { type: 'string', defaultValue: '' },
  aibList: { type: 'array', defaultValue: [] },

  // User ID, Device & Geo
  userIdLastDigitIncludeExclude: { type: 'string', defaultValue: '' },
  userIdLastDigitValues: { type: 'array', defaultValue: [] },
  devicesIncludeExclude: { type: 'string', defaultValue: '' },
  devices: { type: 'array', defaultValue: [] },
  browsersIncludeExclude: { type: 'string', defaultValue: '' },
  browsers: { type: 'array', defaultValue: [] },
  domainsIncludeExclude: { type: 'string', defaultValue: '' },
  domains: { type: 'array', defaultValue: [] },
  operatingSystemIncludeExclude: { type: 'string', defaultValue: '' },
  operatingSystems: { type: 'array', defaultValue: [] },
  statesIncludeExclude: { type: 'string', defaultValue: '' },
  statesValues: { type: 'array', defaultValue: [] },
  zipsIncludeExclude: { type: 'string', defaultValue: '' },
  zipsValues: { type: 'string', defaultValue: '' },

  // Attributes
  userAttributesIncludeExclude: { type: 'string', defaultValue: '' },
  userAttributes: { type: 'object', defaultValue: null },
  urlParametersIncludeExclude: { type: 'string', defaultValue: '' },
  urlParameterRules: { type: 'object', defaultValue: null },
  userAgentIncludeExclude: { type: 'string', defaultValue: '' },
  userAgentRules: { type: 'object', defaultValue: null },
  affiliateNamesIncludeExclude: { type: 'string', defaultValue: '' },
  affiliateNames: { type: 'array', defaultValue: [] },
  affSubIDConfiguration: {
    type: 'object',
    defaultValue: { includeExclude: '', affiliate: '', subIds: '', subIdList: [], andOrLogic: '' },
  },
  domainAffiliates: {
    type: 'object',
    defaultValue: { domainAffiliateEntries: [] },
  },
  qualityScore: {
    type: 'object',
    defaultValue: {
      qualityScoreRange: [],
      bypass: [],
      cap_reset: {
        resetInterval: 'daily',
        resetDayOfWeek: '',
        resetDayOfMonth: '',
        resetDate: '',
      },
    },
  },
  emailDomainsIncludeExclude: { type: 'string', defaultValue: '' },
  emailDomains: { type: 'array', defaultValue: [] },

  // Landing Pages & Questions
  landingPageGroupIncludeExclude: { type: 'string', defaultValue: '' },
  landingPageGroupIds: { type: 'array', defaultValue: [] },
  landingPageIncludeExclude: { type: 'string', defaultValue: '' },
  landingPageIds: { type: 'array', defaultValue: [] },
  question: { type: 'string', defaultValue: '' },
  questionAnswers: { type: 'array', defaultValue: [] },

  // Pre-ping
  internalPrePings: {
    type: 'object',
    defaultValue: {
      andOrLogic: 'AND' as const,
      internalPrePings: [],
    },
  },
  prePingResultSessionCapture: { type: 'boolean', defaultValue: false },
  prePingMethod: { type: 'string', defaultValue: '' },
  prePingHandlerId: { type: 'string', defaultValue: null },
  prePingUrl: { type: 'string', defaultValue: '' },
  prePingBody: { type: 'string', defaultValue: '' },
  prePingTimeout: { type: 'string', defaultValue: '' },
  prePingHeader1: { type: 'string', defaultValue: '' },
  prePingHeader2: { type: 'string', defaultValue: '' },
  prePingHeader3: { type: 'string', defaultValue: '' },
  prePingCustomHttpStatus: { type: 'string', defaultValue: '' },

  // Daily Restrictions
  dailyLimit: { type: 'string', defaultValue: '' },
  dailyLimitAnswers: { type: 'array', defaultValue: [] },
  capInterval: { type: 'string', defaultValue: '' },
  capAmount: { type: 'number|null', defaultValue: null },
  currentCapAmount: { type: 'number', defaultValue: 0 },
  dayHourRestrictions: {
    type: 'string',
    defaultValue: JSON.stringify({
      monday: { restriction: 'Available', restrictionStart: '', restrictionEnd: '' },
      tuesday: { restriction: 'Available', restrictionStart: '', restrictionEnd: '' },
      wednesday: { restriction: 'Available', restrictionStart: '', restrictionEnd: '' },
      thursday: { restriction: 'Available', restrictionStart: '', restrictionEnd: '' },
      friday: { restriction: 'Available', restrictionStart: '', restrictionEnd: '' },
      saturday: { restriction: 'Available', restrictionStart: '', restrictionEnd: '' },
      sunday: { restriction: 'Available', restrictionStart: '', restrictionEnd: '' },
    }),
  },

  // Analysis
  randomFailureProbabilities: { type: 'array', defaultValue: [] },

  // IPQS Configuration
  ipqsConfiguration: {
    type: 'object',
    defaultValue: {
      shouldProcess: false,
      email: '',
      ip: '',
      phone: '',
      fingerprint: '',
    },
  },

  // Cap Dependency Configuration
  capDependency: {
    type: 'object',
    defaultValue: null,
  },

  // Cap Configuration (new - supports Daily, Monthly, Lifetime, and DOW caps)
  capConfiguration: {
    type: 'object',
    defaultValue: null,
  },
};

export function generateDefaultState(): CriteriaState {
  const state = {} as Record<string, unknown>;

  for (const [key, schema] of Object.entries(CRITERIA_STATE_SCHEMA)) {
    state[key] = schema.defaultValue;
  }

  return state as unknown as CriteriaState;
}

// Deep merge utility for nested objects
function deepMerge<T extends Record<string, unknown>>(target: T, source: Partial<T>): T {
  const result = { ...target };

  for (const key in source) {
    if (Object.prototype.hasOwnProperty.call(source, key)) {
      const sourceValue = source[key];
      const targetValue = result[key];

      if (
        sourceValue != null &&
        typeof sourceValue === 'object' &&
        typeof targetValue === 'object' &&
        !Array.isArray(sourceValue)
      ) {
        (result as Record<string, unknown>)[key] = deepMerge(
          targetValue as Record<string, unknown>,
          sourceValue as Record<string, unknown>
        );
      } else if (sourceValue !== undefined) {
        (result as Record<string, unknown>)[key] = sourceValue;
      }
    }
  }

  return result;
}

export function mergeWithParsedConfig(parsedConfig: Partial<CriteriaState>): CriteriaState {
  const defaultState = generateDefaultState();

  // Ensure complex objects have the right structure
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const legacyDayHour = (parsedConfig as any)?.dayhourRestrictions;
  // Support legacy/alternate keys from older implementations
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const legacyCriteriaDayHour = (parsedConfig as any)?.criteriaDayHourRestrictions;
  const rawDayHourRestrictions = parsedConfig.dayHourRestrictions ?? legacyCriteriaDayHour ?? legacyDayHour;

  let dayHourRestrictionsString: string;
  if (typeof rawDayHourRestrictions === 'string') {
    dayHourRestrictionsString = rawDayHourRestrictions;
  } else if (rawDayHourRestrictions && typeof rawDayHourRestrictions === 'object') {
    dayHourRestrictionsString = JSON.stringify(rawDayHourRestrictions);
  } else {
    // Use default value from schema
    dayHourRestrictionsString = CRITERIA_STATE_SCHEMA.dayHourRestrictions.defaultValue as string;
  }

  // Defensive merge that preserves defaults and deep-merges nested objects
  const mergedConfig = { ...defaultState };

  for (const [key, schema] of Object.entries(CRITERIA_STATE_SCHEMA)) {
    const parsedValue = parsedConfig[key as keyof CriteriaState];

    if (parsedValue === undefined) {
      // Keep default value
      continue;
    }

    if (schema.type === 'object' && typeof schema.defaultValue === 'object' && schema.defaultValue !== null) {
      // Deep merge nested objects
      (mergedConfig as Record<string, unknown>)[key] = deepMerge(
        schema.defaultValue as Record<string, unknown>,
        parsedValue as Record<string, unknown>
      );
    } else {
      // For non-objects, use the parsed value if it's not null/undefined
      (mergedConfig as Record<string, unknown>)[key] = parsedValue ?? schema.defaultValue;
    }
  }

  // Special handling for dayHourRestrictions to keep it as string
  mergedConfig.dayHourRestrictions = dayHourRestrictionsString;

  // Backfill new state fields from legacy keys if present
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const anyConfig = parsedConfig as any;
  if (!mergedConfig.dailyLimit && anyConfig?.criteriaDailyLimit != null) {
    // legacy stored number/null; keep as string for state consistency
    (mergedConfig as any).dailyLimit = String(anyConfig.criteriaDailyLimit ?? '');
  }
  if (
    (!mergedConfig.dailyLimitAnswers || mergedConfig.dailyLimitAnswers.length === 0) &&
    Array.isArray(anyConfig?.criteriaDailyLimitAnswers)
  ) {
    (mergedConfig as any).dailyLimitAnswers = anyConfig.criteriaDailyLimitAnswers;
  }

  // Special handling for capDependency
  if (parsedConfig.capDependency) {
    mergedConfig.capDependency = parsedConfig.capDependency;
  }

  // Special handling for capConfiguration - sync DOW cap restrictions from dayHourRestrictions
  if (parsedConfig.capConfiguration) {
    const capConfig = cloneDeep(parsedConfig.capConfiguration);
    
    // Parse dayHourRestrictions to sync restrictions to DOW caps
    let dayHourData: Record<string, { restriction?: string; restrictionStart?: string; restrictionEnd?: string }> = {};
    try {
      if (mergedConfig.dayHourRestrictions) {
        dayHourData = typeof mergedConfig.dayHourRestrictions === 'string' 
          ? JSON.parse(mergedConfig.dayHourRestrictions) 
          : mergedConfig.dayHourRestrictions;
      }
    } catch {
      dayHourData = {};
    }

    // Convert day restriction to CapRestriction
    const toCapRestriction = (
      restriction: string | undefined,
      starts: string | undefined,
      ends: string | undefined
    ): CapRestriction | null => {
      if (restriction === 'Partially Restricted' && starts && ends) {
        return {
          type: RestrictionTypes.PARTIAL,
          starts,
          ends,
        };
      }
      return null;
    };

    // Sync restrictions from dayHourRestrictions to DOW caps
    if (capConfig.caps && Array.isArray(capConfig.caps)) {
      capConfig.caps = capConfig.caps.map((cap: CapInstance) => {
        // Find matching day for this DOW cap type using the reverse mapping
        const dayName = DOW_TYPE_TO_DAY[cap.type];
        if (dayName) {
          const dayRestriction = dayHourData[dayName];
          if (dayRestriction) {
            const capRestriction = toCapRestriction(
              dayRestriction.restriction,
              dayRestriction.restrictionStart,
              dayRestriction.restrictionEnd
            );
            return { ...cap, restriction: capRestriction };
          }
        }
        return cap;
      });
    }
    
    mergedConfig.capConfiguration = capConfig;
  }

  // Special handling for affSubIDConfiguration to ensure proper types and preserve all entries
  // Backend always returns new format: {"andOrLogic":"AND","subIdList":[...]}
  if (!mergedConfig.affSubIDConfiguration) {
    mergedConfig.affSubIDConfiguration = {
      includeExclude: '',
      affiliate: '',
      subIds: '',
      subIdList: [],
      andOrLogic: '' as 'AND' | 'OR' | 'ANY' | '',
    };
  } else {
    const affSubIDConfiguration = mergedConfig.affSubIDConfiguration as any;

    // Process subIdList array from backend
    if (
      affSubIDConfiguration.subIdList &&
      Array.isArray(affSubIDConfiguration.subIdList) &&
      affSubIDConfiguration.subIdList.length > 0
    ) {
      // Normalise each entry's subIds to array<string>
      const normalisedSubIdList = affSubIDConfiguration.subIdList.map((entry: any) => {
        const rawSubIds = entry.subIds;
        const subIdsArray = Array.isArray(rawSubIds)
          ? rawSubIds
          : typeof rawSubIds === 'string' && rawSubIds.trim().length > 0
          ? rawSubIds
              .split('\n')
              .map((line: string) => line.trim())
              .filter((line: string) => line.length > 0)
          : [];
        return {
          includeExclude: entry.includeExclude || '',
          affiliate: entry.affiliate || '',
          subIds: subIdsArray,
        };
      });

      // Check if all entries are empty (no meaningful data)
      const hasAnyValidData = normalisedSubIdList.some((entry: any) => {
        const hasIncludeExclude = entry.includeExclude && entry.includeExclude.trim() !== '';
        const hasAffiliate = entry.affiliate && entry.affiliate.trim() !== '';
        const hasSubIds = entry.subIds && Array.isArray(entry.subIds) && entry.subIds.length > 0;
        return hasIncludeExclude || hasAffiliate || hasSubIds;
      });

      const firstEntry = normalisedSubIdList[0];
      const subIdsString = firstEntry.subIds && firstEntry.subIds.length > 0 ? firstEntry.subIds.join('\n') : '';

      // If no valid data, treat as empty and set andOrLogic to empty string
      if (!hasAnyValidData) {
        mergedConfig.affSubIDConfiguration = {
          includeExclude: '',
          affiliate: '',
          subIds: '',
          subIdList: [],
          andOrLogic: '' as 'AND' | 'OR' | 'ANY' | '',
        };
      } else {
        mergedConfig.affSubIDConfiguration = {
          includeExclude: firstEntry.includeExclude || '',
          affiliate: firstEntry.affiliate || '',
          subIds: subIdsString,
          subIdList: normalisedSubIdList,
          andOrLogic: (() => {
            const raw = String(affSubIDConfiguration.andOrLogic ?? '').toUpperCase();
            if (raw === 'AND' || raw === 'OR' || raw === 'ANY') return raw as 'AND' | 'OR' | 'ANY';
            return 'AND' as const;
          })(),
        };
      }
    } else {
      // Empty or no subIdList - set to default empty state
      mergedConfig.affSubIDConfiguration = {
        includeExclude: '',
        affiliate: '',
        subIds: '',
        subIdList: [],
        andOrLogic: '' as 'AND' | 'OR' | 'ANY' | '',
      };
    }
  }

  // Special handling for zipsValues to ensure proper string type
  if (Array.isArray(mergedConfig.zipsValues)) {
    mergedConfig.zipsValues = (mergedConfig.zipsValues as string[]).join(' ');
  }

  // Landing pages: backend may store numeric IDs; UI expects string IDs
  const mergedConfigRecord = mergedConfig as unknown as Record<string, unknown>;
  const landingPageIds = mergedConfigRecord.landingPageIds;
  if (Array.isArray(landingPageIds)) {
    mergedConfigRecord.landingPageIds = landingPageIds.map((id) => String(id));
  }
  const landingPageGroupIds = mergedConfigRecord.landingPageGroupIds;
  if (Array.isArray(landingPageGroupIds)) {
    mergedConfigRecord.landingPageGroupIds = landingPageGroupIds.map((id) => String(id));
  }

  // Special handling for userIdLastDigitValues to ensure proper string type (preserve leading zeros)
  if (Array.isArray(mergedConfig.userIdLastDigitValues)) {
    mergedConfig.userIdLastDigitValues = (mergedConfig.userIdLastDigitValues as (string | number)[]).map(String);
  }

  // Special handling for qualityScore - convert from backend object format to array format
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const backendQualityScore = (anyConfig as any)?.qualityScore;
  console.log('mergeWithParsedConfig - backendQualityScore:', backendQualityScore);
  console.log('mergeWithParsedConfig - mergedConfig.qualityScore:', mergedConfig.qualityScore);
  if (backendQualityScore) {
    console.log('mergeWithParsedConfig - processing qualityScore');
    const qualityScoreRange: any[] = [];
    // Legacy support: if backend provided flat minScore/maxScore on qualityScore, map it to index_0
    const hasLegacyMinMax =
      (backendQualityScore.minScore !== undefined && backendQualityScore.minScore !== null) ||
      (backendQualityScore.maxScore !== undefined && backendQualityScore.maxScore !== null);

    if (
      backendQualityScore.qualityScoreRange &&
      typeof backendQualityScore.qualityScoreRange === 'object' &&
      !Array.isArray(backendQualityScore.qualityScoreRange)
    ) {
      console.log('mergeWithParsedConfig - converting qualityScoreRange from object to array');
      Object.keys(backendQualityScore.qualityScoreRange)
        .sort((a, b) => {
          // Sort by minScore (descending - highest first), then by key for consistency
          const aData = backendQualityScore.qualityScoreRange[a];
          const bData = backendQualityScore.qualityScoreRange[b];
          const aMinScore = aData?.minScore;
          const bMinScore = bData?.minScore;

          // Convert to numbers for comparison
          const aNum = aMinScore !== null && aMinScore !== undefined && aMinScore !== '' ? parseInt(aMinScore) : null;
          const bNum = bMinScore !== null && bMinScore !== undefined && bMinScore !== '' ? parseInt(bMinScore) : null;

          // If both are null/empty, maintain original order by key
          if (aNum === null && bNum === null) {
            return parseInt(a) - parseInt(b);
          }

          // If only a is null/empty, put it at the end
          if (aNum === null) {
            return 1;
          }

          // If only b is null/empty, put it at the end
          if (bNum === null) {
            return -1;
          }

          // Both have values, sort numerically (descending - highest first)
          return bNum - aNum;
        })
        .forEach((key) => {
          const range = backendQualityScore.qualityScoreRange[key];
          const backendDayFilters = (range as any).dayFilters ?? (range as any).dayFilter;
          let dayFilters: string[] = [];
          if (Array.isArray(backendDayFilters)) {
            dayFilters = backendDayFilters.filter((v: unknown) => typeof v === 'string' && v.length > 0);
          } else if (typeof backendDayFilters === 'string' && backendDayFilters) {
            dayFilters = backendDayFilters
              .split(',')
              .map((s) => s.trim())
              .filter((s) => s.length > 0);
          }

          qualityScoreRange.push({
            name: `index_${key}`,
            minScore: range.minScore ?? null,
            maxScore: range.maxScore ?? null,
            cap: range.cap ?? null,
            // Treat incoming failureProbability as decimal (0-1). Default 0 if missing.
            failureProbability: range.failureProbability ?? 0,
            // Default validMultipliers to 0 if missing for legacy compatibility
            validMultipliers: range.validMultipliers ?? 0,
            dayFilters,
          });
        });
    } else if (hasLegacyMinMax) {
      // Create a single range row at index_0 using legacy min/max
      qualityScoreRange.push({
        name: 'index_0',
        minScore: backendQualityScore.minScore ?? null,
        maxScore: backendQualityScore.maxScore ?? null,
        cap: backendQualityScore.cap ?? null,
        failureProbability: 0,
        validMultipliers: 0,
        dayFilters: [],
      });
    }

    const bypass: any[] = [];
    if (
      backendQualityScore.bypass &&
      typeof backendQualityScore.bypass === 'object' &&
      !Array.isArray(backendQualityScore.bypass)
    ) {
      Object.keys(backendQualityScore.bypass).forEach((key) => {
        const bypassEntry = backendQualityScore.bypass[key];
        bypass.push({
          name: `index_${key}`,
          bypass_affiliate: bypassEntry.bypass_affiliate || [],
          cap: bypassEntry.cap ?? null,
          failureProbability: bypassEntry.failureProbability ?? null,
        });
      });
    }

    mergedConfig.qualityScore = {
      qualityScoreRange,
      bypass,
      cap_reset: backendQualityScore.cap_reset || {
        resetInterval: 'daily',
        resetDayOfWeek: '',
        resetDayOfMonth: '',
        resetDate: '',
      },
    };
    console.log('mergeWithParsedConfig - mergedConfig.qualityScore after conversion:', mergedConfig.qualityScore);
  }

  // Legacy root-level min/max handling (no qualityScore object provided)
    if (
      !mergedConfig.qualityScore?.qualityScoreRange ||
      (mergedConfig.qualityScore.qualityScoreRange as any[])?.length === 0
    ) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const legacyMin = (anyConfig as any)?.minScore ?? null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const legacyMax = (anyConfig as any)?.maxScore ?? null;
    if (legacyMin !== null || legacyMax !== null) {
      mergedConfig.qualityScore = mergedConfig.qualityScore || ({} as any);
      (mergedConfig.qualityScore as any).qualityScoreRange = [
        {
          name: 'index_0',
          minScore: legacyMin,
          maxScore: legacyMax,
          cap: null,
          failureProbability: 0,
          validMultipliers: 0,
          dayFilters: [],
        },
      ];
      (mergedConfig.qualityScore as any).bypass = (mergedConfig.qualityScore as any).bypass || [];
      (mergedConfig.qualityScore as any).cap_reset = (mergedConfig.qualityScore as any).cap_reset || {
        resetInterval: 'daily',
        resetDayOfWeek: '',
        resetDayOfMonth: '',
        resetDate: '',
      };
    }
  }

  return mergedConfig as CriteriaState;
}

export function transformStateForBackend(state: CriteriaState): any {
  const backendState = cloneDeep(state);

  // Handle prePingHandlerId - ensure it's either a valid handler object or null
  if (backendState.prePingHandlerId) {
    // If it's already an object with an id, keep it as is
    if (typeof backendState.prePingHandlerId === 'object' && backendState.prePingHandlerId.id) {
      // Already correct format
    }
    // If it's a string (legacy), convert to null for now (should be handled by UI)
    else if (typeof backendState.prePingHandlerId === 'string' && backendState.prePingHandlerId.trim() === '') {
      backendState.prePingHandlerId = null;
    }
  } else {
    // Ensure it's explicitly null if falsy
    backendState.prePingHandlerId = null;
  }

  const isValidPrePingType = (entry: any) => {
    const type = entry?.prePingSuppressionType;
    return type && ['days', 'hours', 'minutes'].includes(String(type).toLowerCase());
  };

  if (
    backendState.internalPrePings?.internalPrePings &&
    Array.isArray(backendState.internalPrePings.internalPrePings)
  ) {
    backendState.internalPrePings.internalPrePings =
      backendState.internalPrePings.internalPrePings.filter(isValidPrePingType);

    if (backendState.internalPrePings.internalPrePings.length === 0) {
      (backendState as any).internalPrePings = null;
    }
  } else if (backendState.internalPrePings && !backendState.internalPrePings.internalPrePings) {
    (backendState as any).internalPrePings = null;
  }

  // Transform affSubIDConfiguration for backend
  if (backendState.affSubIDConfiguration) {
    const config = backendState.affSubIDConfiguration as any;

    // Check if andOrLogic is empty - if so, delete the configuration (save as null)
    const hasEmptyAndOrLogic =
      !config.andOrLogic || config.andOrLogic === '' || config.andOrLogic === null || config.andOrLogic === undefined;

    // If "Select Logic" is selected (empty andOrLogic), delete the configuration so backend receives null
    if (hasEmptyAndOrLogic) {
      delete (backendState as any).affSubIDConfiguration;
    } else {
      // Ensure andOrLogic is uppercase
      if (config.andOrLogic && typeof config.andOrLogic === 'string') {
        (backendState.affSubIDConfiguration as any).andOrLogic = config.andOrLogic.toUpperCase();
      }

      // Transform subIds if present
      const { subIds } = config;
      if (typeof subIds === 'string' && subIds.trim().length > 0) {
        // Convert non-empty string to array for backend processing
        (backendState.affSubIDConfiguration as any).subIds = subIds
          .split('\n')
          .map((s: string) => s.trim())
          .filter((s: string) => s.length > 0);
      } else if (Array.isArray(subIds)) {
        (backendState.affSubIDConfiguration as any).subIds = subIds
          .map((s: string) => s.trim())
          .filter((s: string) => s.length > 0);
        if ((backendState.affSubIDConfiguration as any).subIds.length === 0) {
          delete (backendState.affSubIDConfiguration as any).subIds;
        }
      } else {
        // Do not send a value for subIds if input is empty or not set
        delete (backendState.affSubIDConfiguration as any).subIds;
      }

      // Transform subIdList entries - convert subIds from string to array
      if (config.subIdList && Array.isArray(config.subIdList)) {
        (backendState.affSubIDConfiguration as any).subIdList = config.subIdList
          .map((entry: any) => {
            const entrySubIds = entry.subIds;
            if (typeof entrySubIds === 'string' && entrySubIds.trim().length > 0) {
              return {
                ...entry,
                subIds: entrySubIds
                  .split('\n')
                  .map((s: string) => s.trim())
                  .filter((s: string) => s.length > 0),
              };
            } else if (Array.isArray(entrySubIds)) {
              return {
                ...entry,
                subIds: entrySubIds.map((s: string) => s.trim()).filter((s: string) => s.length > 0),
              };
            }
            return entry;
          })
          .filter((entry: any) => {
            const isNullAffiliate =
              typeof entry.affiliate === 'string' && entry.affiliate.trim().toLowerCase() === '[null]';
            const hasIncludeExclude =
              entry.includeExclude &&
              entry.includeExclude.trim() !== '' &&
              entry.includeExclude.toLowerCase() !== 'none';
            const hasAffiliate = entry.affiliate && entry.affiliate.trim() !== '';
            const hasSubIds = entry.subIds && Array.isArray(entry.subIds) && entry.subIds.length > 0;
            return hasIncludeExclude && hasAffiliate && (hasSubIds || isNullAffiliate);
          });

        // If subIdList is empty after filtering, treat as if "Select Logic" was selected (save as null)
        if ((backendState.affSubIDConfiguration as any).subIdList.length === 0) {
          delete (backendState as any).affSubIDConfiguration;
        }
      } else {
        // If subIdList doesn't exist or is not an array, treat as if "Select Logic" was selected (save as null)
        delete (backendState as any).affSubIDConfiguration;
      }
    }
  }

  // Transform domainAffiliates for backend
  if (backendState.domainAffiliates?.domainAffiliateEntries) {
    backendState.domainAffiliates = {
      ...backendState.domainAffiliates,
      domainAffiliateEntries: backendState.domainAffiliates.domainAffiliateEntries.map((entry: any) => {
        const processedDomains = entry.domains?.map((domain: any) =>
          typeof domain === 'object' && domain.value
            ? domain.value
            : typeof domain === 'object' && domain.name
            ? domain.name
            : domain
        );

        const processedAffiliates = entry.affiliates?.map((affiliate: any) =>
          typeof affiliate === 'object' && affiliate.value
            ? affiliate.value
            : typeof affiliate === 'object' && affiliate.name
            ? affiliate.name
            : affiliate
        );

        return {
          ...entry,
          domains: processedDomains,
          affiliates: processedAffiliates,
        };
      }),
    };
  }

  // Map to legacy keys so saved data matches older DailyRestrictionPane.jsx
  // Coerce to number when possible, otherwise null
  if (backendState.dailyLimit === '' || backendState.dailyLimit === undefined || backendState.dailyLimit === null) {
    (backendState as any).criteriaDailyLimit = null;
  } else {
    {
      const n = Number(backendState.dailyLimit);
      (backendState as any).criteriaDailyLimit = Number.isFinite(n) ? n : null;
    }
  }
  (backendState as any).criteriaDailyLimitAnswers =
    (backendState.dailyLimitAnswers?.length ?? 0) > 0 ? backendState.dailyLimitAnswers : null;
  // dayHourRestrictions is already kept as a JSON string in state
  (backendState as any).criteriaDayHourRestrictions = backendState.dayHourRestrictions || null;

  // Ensure capDependency is properly sent to backend
  // If it has values, keep it; otherwise explicitly set to null
  if (backendState.capDependency) {
    const config = backendState.capDependency;
    const hasOffers = config.offerIds && config.offerIds.length > 0;
    const hasActions = config.offerActionIds && config.offerActionIds.length > 0;

    if (!hasOffers && !hasActions) {
      // No dependencies configured, send null
      backendState.capDependency = null;
    }
    // else: keep the configuration as-is
  } else {
    // Explicitly set to null if not defined
    backendState.capDependency = null;
  }

  // Transform capConfiguration - remove DOW caps for days marked as "Unavailable"
  if (backendState.capConfiguration && backendState.capConfiguration.caps) {
    // Parse dayHourRestrictions to find unavailable days
    let dayHourData: Record<string, { restriction?: string }> = {};
    try {
      if (backendState.dayHourRestrictions) {
        dayHourData = typeof backendState.dayHourRestrictions === 'string'
          ? JSON.parse(backendState.dayHourRestrictions)
          : backendState.dayHourRestrictions;
      }
    } catch {
      dayHourData = {};
    }

    // Filter out DOW caps where the day is marked as "Unavailable"
    backendState.capConfiguration.caps = backendState.capConfiguration.caps.filter((cap: CapInstance) => {
      const dayName = DOW_TYPE_TO_DAY[cap.type];
      if (dayName) {
        const dayRestriction = dayHourData[dayName];
        // If day is Unavailable, remove this DOW cap
        if (dayRestriction?.restriction === 'Unavailable') {
          return false;
        }
      }
      // Keep all non-DOW caps (MONTHLY, LIFETIME) and available DOW caps
      return true;
    });
  }

  // Transform qualityScore from array format to object format for backend
  if (backendState.qualityScore) {
    const qualityScore = backendState.qualityScore;

    // Transform qualityScoreRange from array to object indexed by redis key
    const rangeObj: any = {};
    if (qualityScore.qualityScoreRange && Array.isArray(qualityScore.qualityScoreRange)) {
      qualityScore.qualityScoreRange.forEach((range: any) => {
        const nameMatch = range.name?.match(/index_(\d+)/);
        const keyIndex = nameMatch ? nameMatch[1] : range.name;

        const hasAnyField =
          range.minScore !== null ||
          range.maxScore !== null ||
          range.cap !== null ||
          range.failureProbability !== null ||
          range.validMultipliers !== null ||
          (Array.isArray(range.dayFilters) && range.dayFilters.length > 0);

        if (hasAnyField) {
          rangeObj[keyIndex] = {
            minScore: range.minScore,
            maxScore: range.maxScore,
            cap: range.cap,
            failureProbability: range.failureProbability,
            validMultipliers: range.validMultipliers,
            dayFilters: Array.isArray(range.dayFilters)
              ? range.dayFilters.filter((v: unknown) => typeof v === 'string' && v.length > 0)
              : [],
          };
        }
      });
    }
    // Legacy fallback: if no ranges were provided but legacy min/max exist on qualityScore, create index 0
    if (
      Object.keys(rangeObj).length === 0 &&
      (qualityScore.minScore !== undefined || qualityScore.maxScore !== undefined)
    ) {
      const legacyMin = qualityScore.minScore ?? null;
      const legacyMax = qualityScore.maxScore ?? null;
      if (legacyMin !== null || legacyMax !== null) {
        rangeObj['0'] = {
          minScore: legacyMin,
          maxScore: legacyMax,
          cap: null,
          // Defaults for legacy
          failureProbability: 0,
          validMultipliers: 0,
        } as any;
      }
    }

    // Legacy root-level min/max fallback when qualityScore has no min/max
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rootLegacyMin: any = (backendState as any).minScore ?? null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rootLegacyMax: any = (backendState as any).maxScore ?? null;
    if (Object.keys(rangeObj).length === 0 && (rootLegacyMin !== null || rootLegacyMax !== null)) {
      rangeObj['0'] = {
        minScore: rootLegacyMin,
        maxScore: rootLegacyMax,
        cap: null,
        failureProbability: 0,
        validMultipliers: 0,
      } as any;
    }

    // Transform bypass from array to object indexed by redis key
    // Match handleCriteriaSubmit format: convert affiliate objects to numeric IDs
    const bypassObj: any = {};
    if (qualityScore.bypass && Array.isArray(qualityScore.bypass)) {
      qualityScore.bypass.forEach((bypass: any) => {
        const nameMatch = bypass.name?.match(/index_(\d+)/);
        const keyIndex = nameMatch ? nameMatch[1] : bypass.name;

        if (
          (bypass.bypass_affiliate && bypass.bypass_affiliate.length > 0) ||
          bypass.cap !== null ||
          bypass.failureProbability !== null
        ) {
          // Convert affiliate objects to numeric IDs (matching handleCriteriaSubmit)
          const affiliateIds =
            bypass.bypass_affiliate
              ?.map((affiliate: any) => {
                if (typeof affiliate === 'object' && affiliate.id) {
                  return affiliate.id;
                }
                return affiliate;
              })
              .filter((id: any) => id !== null && id !== undefined) || [];

          bypassObj[keyIndex] = {
            bypass_affiliate: affiliateIds,
            cap: bypass.cap,
            failureProbability: bypass.failureProbability,
          };
        }
      });
    }

    // Replace qualityScore with transformed object
    // For offers, use 'qualityScore' key (same as original minScore/maxScore) but with new body structure
    (backendState as any).qualityScore = {
      qualityScoreRange: Object.keys(rangeObj).length > 0 ? rangeObj : {},
      bypass: Object.keys(bypassObj).length > 0 ? bypassObj : {},
      cap_reset: qualityScore.cap_reset || {
        resetInterval: 'daily',
        resetDayOfWeek: '',
        resetDayOfMonth: '',
        resetDate: '',
      },
    };

    // Clean any legacy min/max keys on qualityScore and root
    delete (backendState as any).qualityScore.minScore;
    delete (backendState as any).qualityScore.maxScore;
    delete (backendState as any).minScore;
    delete (backendState as any).maxScore;
  }

  return backendState;
}

export function validateStateField(key: keyof CriteriaState, value: unknown): boolean {
  const schema = CRITERIA_STATE_SCHEMA[key];
  if (!schema) return false;

  switch (schema.type) {
    case 'boolean':
      return typeof value === 'boolean';
    case 'string':
      return typeof value === 'string';
    case 'number':
      return typeof value === 'number';
    case 'number|null':
      return typeof value === 'number' || value === null;
    case 'array':
      return Array.isArray(value);
    case 'object':
      return typeof value === 'object' && value !== null;
    default:
      return false;
  }
}
